// Homepage.jsx

// FULL UPDATED FILE (uses props & no dummy API logic)

import React from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Stack,
  useTheme,
  InputAdornment,
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import AssessmentIcon from "@mui/icons-material/Assessment";
import TimelineIcon from "@mui/icons-material/Timeline";
import CorporateFareIcon from "@mui/icons-material/CorporateFare";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";

const Homepage = ({
  searchValue,
  onSearchChange,
  onSearchSubmit,
  isAnalyzing,
}) => {
  const theme = useTheme();

  const handleAnalyzeClick = () => {
    if (onSearchSubmit) {
      onSearchSubmit(searchValue || "");
    }
  };

  return (
    <Box
      sx={{
        height: "96vh",
        display: "flex",
        bgcolor: theme.palette.mode === "dark" ? "#0a192f" : "#f0f4f8",
        color: theme.palette.mode === "dark" ? "#e5e7eb" : "#1f2933",
        fontFamily: "Roboto, sans-serif",
        mt: 3,
        overflow: "hidden",
      }}
    >
      {/* LEFT AREA */}
      <Box
        component="main"
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: { xs: 2, md: 4 },
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 960 }}>
          {/* Header */}
          <Box sx={{ textAlign: "center", mb: 2 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                mb: 0.8,
                color: theme.palette.mode === "dark" ? "#ffffff" : "#111827",
              }}
            >
              Your Client Intelligence Engine
            </Typography>

            <Typography
              variant="body1"
              sx={{
                maxWidth: 620,
                mx: "auto",
                color: theme.palette.mode === "dark" ? "#d1d5db" : "#4b5563",
              }}
            >
              Search any organization to get a single, consolidated brief —
              including profile, portfolio, risks, financials, news signals, and
              AI-generated next best actions.
            </Typography>
          </Box>

          {/* Search */}
          <Box
            sx={{
              mb: 2,
              display: "flex",
              alignItems: "center",
              gap: 1,
              p: 1,
              borderRadius: 2,
              border: "1px solid",
              boxShadow: 1,
              bgcolor:
                theme.palette.mode === "dark"
                  ? theme.palette.background.paper
                  : "#ffffff",
              borderColor:
                theme.palette.mode === "dark" ? "#374151" : "#e5e7eb",
            }}
          >
            <TextField
              autoComplete="off"
              fullWidth
              variant="standard"
              placeholder="Search by organization / company / institution"
              value={searchValue}
              onChange={(e) => onSearchChange && onSearchChange(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#9ca3af" }} />
                  </InputAdornment>
                ),
                disableUnderline: true,
              }}
            />

            <Button
              onClick={handleAnalyzeClick}
              variant="contained"
              color="primary"
              disabled={isAnalyzing || !searchValue}
              sx={{ px: 3, py: 1.1, fontWeight: 600, borderRadius: 1.5 }}
            >
              {isAnalyzing ? "Analyzing…" : "Analyze"}
            </Button>
          </Box>

          {/* Info Cards (just descriptive, no data) */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: 2,
            }}
          >
            <Card sx={{ borderRadius: 2, boxShadow: 1, bgcolor: "#ffffff" }}>
              <CardContent sx={{ p: 2.4 }}>
                <Stack direction="row" spacing={2}>
                  <Box
                    sx={{
                      width: 34,
                      height: 34,
                      borderRadius: "10px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: "rgba(0,51,102,0.08)",
                      color: theme.palette.primary.main,
                    }}
                  >
                    <CorporateFareIcon />
                  </Box>

                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: "0.95rem" }}>
                      Entity Profile (Core 360)
                    </Typography>
                    <Typography
                      sx={{ mt: 0.4, fontSize: "0.8rem", color: "#4b5563" }}
                    >
                      Client segment, financials, decision makers, flags, and
                      core relationship intelligence.
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>

            <Card sx={{ borderRadius: 2, boxShadow: 1, bgcolor: "#ffffff" }}>
              <CardContent sx={{ p: 2.4 }}>
                <Stack direction="row" spacing={2}>
                  <Box
                    sx={{
                      width: 34,
                      height: 34,
                      borderRadius: "10px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: "rgba(0,51,102,0.08)",
                      color: theme.palette.primary.main,
                    }}
                  >
                    <AssessmentIcon />
                  </Box>

                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: "0.95rem" }}>
                      Portfolio & Mandates
                    </Typography>
                    <Typography
                      sx={{ mt: 0.4, fontSize: "0.8rem", color: "#4b5563" }}
                    >
                      Book of business, AUM mix, investment health, peer gaps,
                      mandate signals and trends.
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>

            <Card sx={{ borderRadius: 2, boxShadow: 1, bgcolor: "#ffffff" }}>
              <CardContent sx={{ p: 2.4 }}>
                <Stack direction="row" spacing={2}>
                  <Box
                    sx={{
                      width: 34,
                      height: 34,
                      borderRadius: "10px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: "rgba(0,51,102,0.08)",
                      color: theme.palette.primary.main,
                    }}
                  >
                    <TimelineIcon />
                  </Box>

                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: "0.95rem" }}>
                      Engagement Timeline (Consultant View)
                    </Typography>
                    <Typography
                      sx={{ mt: 0.4, fontSize: "0.8rem", color: "#4b5563" }}
                    >
                      Interaction depth, upcoming meetings, engagement dips, and
                      key moments that impact the relationship.
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>

            <Card sx={{ borderRadius: 2, boxShadow: 1, bgcolor: "#ffffff" }}>
              <CardContent sx={{ p: 2.4 }}>
                <Stack direction="row" spacing={2}>
                  <Box
                    sx={{
                      width: 34,
                      height: 34,
                      borderRadius: "10px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: "rgba(0,51,102,0.08)",
                      color: theme.palette.primary.main,
                    }}
                  >
                    <AutoAwesomeIcon />
                  </Box>

                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: "0.95rem" }}>
                      AI Recommendations
                    </Typography>
                    <Typography
                      sx={{ mt: 0.4, fontSize: "0.8rem", color: "#4b5563" }}
                    >
                      Actionable next steps, risks, opportunities, scripts,
                      alerts & engagement guidance.
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Box>
        </Box>
      </Box>

      {/* RIGHT SIDEBAR (unchanged, descriptive only) */}
      <Box
        component="aside"
        sx={{
          width: { xs: 0, md: 360 },
          display: { xs: "none", md: "flex" },
          flexDirection: "column",
          height: "96vh",
          px: 3,
          py: 3,
          overflow: "hidden",
          justifyContent: "space-between",
          background: "linear-gradient(135deg, #002147 0%, #003366 100%)",
          color: "#ffffff",
          pt: 7,
        }}
      >
        <Box sx={{ flexGrow: 1 }}>
          {[
            {
              title: "Unified Intelligence",
              text: "One consolidated view of profile, portfolio, risks, news, performance and engagement health.",
            },
            {
              title: "Consultant Perspective",
              text: "How the client is positioned, expected review cycles, influence of ratings and mandate signals.",
            },
            {
              title: "Engagement Strength",
              text: "Touchpoint frequency, momentum shifts and upcoming interaction priorities.",
            },
            {
              title: "Risk Detection",
              text: "AI-flagged investment, liquidity, fee, service or governance risks requiring attention.",
            },
            {
              title: "AI Narrative Engine",
              text: "Scripts, insights, and suggested actions tailored to each client’s context.",
            },
          ].map((item, index, arr) => (
            <Box key={index} sx={{ py: 1.4 }}>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "0.95rem",
                  color: "#ffffff",
                }}
              >
                {item.title}
              </Typography>

              <Typography
                sx={{
                  mt: 0.4,
                  fontSize: "0.82rem",
                  color: "#ffffff",
                }}
              >
                {item.text}
              </Typography>

              {index < arr.length - 1 && (
                <Box
                  sx={{
                    mt: 1.3,
                    height: 1,
                    bgcolor: "rgba(255,255,255,0.25)",
                  }}
                />
              )}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default Homepage;
